import { useEffect, useRef } from 'react';
import io from 'socket.io-client';
import { config } from '../config/config';
import { useToast } from '../contexts/ToastContext';
import { msalInstance, apiTokenRequest } from '../services/authConfig';
import { refreshMarketConditions } from '../services/marketConditionsStore';

type Socket = ReturnType<typeof io>;

/** Payload of the `market_conditions_update` broadcast (see socketio_service.py). */
export interface MarketConditionsUpdate {
  update_type?: string;
  market: string;
  author: string;
  action: 'updated' | 'cleared';
  updatedAt?: number;
  version?: number;
}

interface Options {
  /** Connect the socket. False tears any existing connection down. */
  enabled: boolean;
  /** Open the market conditions popover on the given market (toast click). */
  onOpen: (market: string) => void;
  /**
   * Raise the toast. Off silences it WITHOUT touching the socket or the store
   * refresh below, so the header pill still updates -- the same split the
   * repair-order preference makes between its toasts and its nav badge.
   * Defaults ON so a caller that has no preference wired behaves as before.
   */
  showToasts?: boolean;
}

/**
 * Raises a persistent toast for every user when someone saves a market note,
 * and refreshes the shared notes store so the header pill updates at once.
 *
 * Mirrors useDispatchBoardPush: websocket-only, token from MSAL, and every
 * failure degrades to "no toast" because the store's 60s poll still runs.
 * One toast per market: a later change to the same market replaces the
 * earlier toast rather than stacking. The author never toasts themselves.
 */
export function useMarketConditionsPush({ enabled, onOpen, showToasts = true }: Options): void {
  const { showToast, dismissToast } = useToast();
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  const toastByMarket = useRef<Record<string, number>>({});
  // Read through a ref so flipping the preference does not tear the socket
  // down and rebuild it -- the effect below deliberately does not depend on it.
  const showToastsRef = useRef(showToasts);
  showToastsRef.current = showToasts;

  useEffect(() => {
    const realtimeEnabled = import.meta.env.VITE_REALTIME_ENABLED !== 'false';
    if (!enabled || !realtimeEnabled) return;

    let socket: Socket | null = null;
    let cancelled = false;

    const handle = (data: MarketConditionsUpdate) => {
      // Always, whatever the preference: this is what moves the header pill.
      void refreshMarketConditions();
      if (!showToastsRef.current) return;
      const me = (msalInstance.getAllAccounts()[0]?.username ?? '').toLowerCase();
      if (me && data.author?.toLowerCase() === me) return;
      const previous = toastByMarket.current[data.market];
      if (previous !== undefined) dismissToast(previous);
      const id = showToast({
        message: `${data.market} market conditions ${data.action === 'cleared' ? 'cleared' : 'updated'} by ${data.author}`,
        type: 'info',
        persistent: true,
        onClick: () => {
          dismissToast(toastByMarket.current[data.market]);
          delete toastByMarket.current[data.market];
          onOpenRef.current(data.market);
        },
        onDismiss: () => {
          if (toastByMarket.current[data.market] === id) delete toastByMarket.current[data.market];
        },
      });
      toastByMarket.current[data.market] = id;
    };

    const initializeSocket = async () => {
      try {
        const accounts = msalInstance.getAllAccounts();
        if (!accounts || accounts.length === 0) return;
        const response = await msalInstance.acquireTokenSilent({ ...apiTokenRequest, account: accounts[0] });
        if (!response.accessToken || cancelled) return;

        socket = io(config.API_URL.replace('/api/v1', ''), {
          path: '/ws',
          auth: { token: `Bearer ${response.accessToken}` },
          transports: ['websocket'],
          reconnection: true,
          reconnectionDelay: 2000,
          reconnectionDelayMax: 10000,
          reconnectionAttempts: 3,
          timeout: 10000,
        });
        if (cancelled) { socket.disconnect(); socket = null; return; }

        socket.on('market_conditions_update', handle);
        socket.on('connect_error', (error: Error) => {
          console.warn('[Socket.IO MarketConditions] Connection error:', error.message);
        });
      } catch (error) {
        console.warn('[Socket.IO MarketConditions] Failed to initialize socket:', error);
      }
    };

    void initializeSocket();
    return () => {
      cancelled = true;
      if (socket) { socket.disconnect(); socket = null; }
    };
  }, [enabled, showToast, dismissToast]);
}
